import { createModel } from '@rematch/core';
import { push, replace } from 'connected-react-router';
import _ from 'lodash';
import jwt from 'jsonwebtoken';

import { RootModel } from '../index';
import { get, post } from '../../api/api';
import { apiUrls, replaceUrls } from '../../api/apiUrls';
import AuthState from '../../interfaces/AuthState';
import { checkError } from 'src/utils/utils';

export default createModel<RootModel>()({
  state: {
    authenticated: !!localStorage.getItem('token'),
    justLoggedIn: false,
    email: '',
    emailStatus: '',
    phoneStatus: '',
    registered: false,
    changedPassword: false,
    confirmationMessage: false,
    accountConfirmed: false,
    customer: {},
    customerId: localStorage.getItem('customerId'),
    tillUser: null,
    tradingUser: null,
    localWalletSelected:null,
    historySelectedWallet: null,
    customerCard: null,
    tillWallets: []
  } as AuthState,

  reducers: {
    setLogin(state, customer: { [key: string]: any }) {
      const wallets = _.sortBy(customer.wallets, 'typeCode');
      customer.wallets = wallets;

      return { ...state, authenticated: true,
        customer, customerId: customer && customer.id,
        ...(wallets && { localWalletSelected: _.first(_.filter(wallets, w => w.type !== 'pending')) })
      };
    },

    setHistoryWallet(state, wallet:  AuthState['historySelectedWallet']) {
      return {
        ...state,
        historySelectedWallet: wallet
      };
    },

    setCustomerInfo(state, customer: { [key: string]: any }) {
      const wallets = _.sortBy(customer.wallets, 'typeCode') || JSON.parse(localStorage.getItem('wallets'));

      return {
        ...state,
        authenticated: true,
        customer: {
          ...state.customer,
          ...customer,
          wallets: wallets || []
        },
        customerId: customer && customer.id,
        ...(!state.localWalletSelected && { localWalletSelected: _.first(_.filter(wallets, w => w.type !== 'pending')) })
      };
    },

    setJustLoggedIn(state) {
      return { ...state, justLoggedIn: true };
    },

    setLogout(state) {
      return { ...state, authenticated: false, justLoggedIn: false, registered: false, customer: {}, customerId: null };
    },

    setRegister(state, registered: boolean) {
      return { ...state, registered };
    },

    setEmail(state, email: string) {
      return { ...state, email };
    },

    setEmailStatus(state, emailStatus: string) {
      return { ...state, emailStatus };
    },

    setPhoneStatus(state, phoneStatus: string){
      return { ...state, phoneStatus };
    },

    addTillWallets(state, payload: { [key: string]: any }[]){
      return {
        ...state,
        tillWallets: payload
      };
    },

    setConfirmationMessaged(state, confirmationMessage: boolean) {
      return { ...state, confirmationMessage };
    },

    setAccountConfirmed(state, accountConfirmed: boolean) {
      return { ...state, accountConfirmed };
    },

    setChangePassword(state, changedPassword: boolean) {
      return { ...state, changedPassword };
    },

    setWallets(state, payload: { [key: string]: any }[]) {
      return { ...state, customer: { ...state.customer, wallets: payload } };
    },

    setCustomerCard(state, payload: string){
      return { ...state, customerCard: payload };
    },

    setLocalWallet(state, payload: AuthState['localWalletSelected']) {
      return { ...state, localWalletSelected: payload };
    },

    setTillLogin(state, payload: string) {
      return { ...state, tillUser: payload };
    },

    setTradingLogin(state, payload: string) {
      return { ...state, tradingUser: payload };
    }

  },

  effects: (dispatch) => ({
    async login(payload, rootState): Promise<void> {
      const response = await post(apiUrls.login, payload);

      if (response && response.status === 200 && !response.data.results.customer.resetPassword) {
        const { token, customer } = response.data.results;
        const wallets = _.sortBy(customer.wallets, 'typeCode');

        localStorage.setItem('token', token);
        localStorage.setItem('customerId', customer.id);
        localStorage.setItem('wallets', JSON.stringify(wallets));

        if (customer.locale) {
          dispatch.odds.updateLanguage(customer.locale);
        }

        dispatch.auth.setLogin(customer);
        dispatch.auth.setJustLoggedIn();
        dispatch.content.modalType('');
        dispatch(replace(rootState.router.location.pathname + rootState.router.location.search));

        dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
      } else if (response && response.status === 200 && response.data.results.customer.resetPassword){
        dispatch.content.modalType('#new-password');
      } else if (response && response.data.message.includes('blocked')){
        dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) });
        dispatch(replace(rootState.router.location.pathname + rootState.router.location.search + '#blocked-customer'));
      } else if (response.data.results?.customer?.changePassword){
        dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) });
        dispatch(replace(rootState.router.location.pathname + rootState.router.location.search + '#forgot-password'));
      } else if (response && response.data.message.includes('activated')){
        dispatch.auth.setEmail(payload.username);
        dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) });
        dispatch(replace(rootState.router.location.pathname + rootState.router.location.search + '#register-confirmation'));
      } else {
        dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    async loginNoRedirect(payload): Promise<void> {
      const response = await post(apiUrls.login, payload);

      if (response && response.status === 200) {
        const { token, customer } = response.data.results;
        localStorage.setItem('token', token);
        localStorage.setItem('customerId', customer.id);
        localStorage.setItem('wallets', JSON.stringify(customer.wallets));

        dispatch.auth.setLogin(customer);
        dispatch.auth.setJustLoggedIn();
        dispatch.content.modalType('');

        dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
      } else {
        dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    async userByAccount(payload, rootState): Promise<void> {
      try {
        const { accountNumber, username, cardNumber, password } = payload;
        const response = await post(apiUrls.loginRetail, {
          ...(username && { username }),
          ...(accountNumber && { accountNumber }),
          ...(cardNumber && { cardNumber }),
          password });

        if (response && response.status === 200) {
          const { customer } = response.data.results;
          localStorage.setItem('customerId', customer.id);
          localStorage.setItem('wallets', JSON.stringify(customer.wallets));

          if (customer.locale) {
            dispatch.odds.updateLanguage(customer.locale);
          }

          dispatch.auth.setLogin(customer);
          dispatch.content.modalType('');
          dispatch(replace(rootState.router.location.pathname + rootState.router.location.search));

          dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
        } else {
          dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) || 'Oops! Something went wrong.' });
        }
      } catch (error) {
        // TODO: Handle error
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
      }
    },

    async userByPin(payload): Promise<void> {
      try {
        const { cardNumber, password } = payload;
        const response = await post(apiUrls.loginRetail, {
          ...(cardNumber && { cardNumber }),
          password });

        if (response && response.status === 200) {
          const { customer } = response.data.results;
          localStorage.setItem('customerId', customer.id);
          localStorage.setItem('wallets', JSON.stringify(customer.wallets));

          if (customer.locale) {
            dispatch.odds.updateLanguage(customer.locale);
          }

          dispatch.auth.setLogin(customer);
          dispatch.content.modalType('');
          dispatch.content.setShowSecondScreen('');

          dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
          return null;
        } else {
          dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) || 'Oops! Something went wrong.' });
          return response?.data?.message ||  'Oops! Something went wrong.';
        }
      } catch (error) {
        // TODO: Handle error
        dispatch.toast.showToast({ type: 'error', message: 'Oops! Something went wrong.' });
        return null;
      }
    },

    async register(payload): Promise<void> {
      const href = window.location.href;
      const url = href[href.length -1] === '/' ? href.substring(0, href.length - 1) : href;

      const response = await post(apiUrls.register, { ...payload, url });

      if (response && response.status === 201) {
        dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
        dispatch.auth.setRegister(true);
        dispatch.auth.setEmail(payload.email);
      } else {
        dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    async registerRetail(payload): Promise<void> {
      const href = window.location.href;
      const url = href[href.length -1] === '/' ? href.substring(0, href.length - 1) : href;

      const newPayload = { ...payload, company: { id: process.env.REACT_APP_COMPANY_ID }, url };
      const response = await post(apiUrls.retailRegister, newPayload);

      if (response && response.status === 201) {
        dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
        dispatch.auth.setRegister(true);
        dispatch(replace('#register-confirmation'));
      } else {
        const message = checkError(response.data.results);
        dispatch.toast.showToast({ type: 'error', message });
      }
    },

    async registerRewards(payload): Promise<void> {
      const newPayload = { ...payload, company: { id: process.env.REACT_APP_COMPANY_ID } };
      const response = await post(apiUrls.retailRewards, newPayload);

      if (response && response.status === 201) {
        dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
        dispatch.auth.setRegister(true);
      } else {
        dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    async forgotPassword(payload): Promise<void> {
      const response = await post(apiUrls.forgotPassword, payload);

      if (response && response.status === 200) {
        dispatch.auth.setConfirmationMessaged(true);
        dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
      } else {
        dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    async confirmationMessage(): Promise<void> {
      dispatch.auth.setConfirmationMessaged(false);
    },

    async accountConfirmation(): Promise<void> {
      dispatch.auth.setAccountConfirmed(true);
    },

    async confirmAccount(): Promise<void> {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const token = urlParams.get('token');

      if (token === null) {
        return;
      }

      const response = await post(replaceUrls(apiUrls.accountActivation, { token }));

      if (response && response.status === 200) {
        dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
      } else {
        dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    async resendConfirmationEmail(payload, state): Promise<void> {
      const href = window.location.href;
      const url = href[href.length -1] === '/' ? href.substring(0, href.length - 1) : href;

      const response = await post(apiUrls.resendEmailConfirmation, { email: state.auth.email, url });

      if (response && response.status === 200) {
        dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
      } else {
        dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    async resetPassword(payload): Promise<void> {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const token = urlParams.get('token');
      const { password } = payload;

      if (token === null) {
        return;
      }

      const response = await post(replaceUrls(apiUrls.resetPassword, { token }), { password });

      if (response && response.status === 200) {
        dispatch.auth.setChangePassword(true);
        dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
      } else {
        const errors = [];
        const validation = response.data.results.validation;

        for (const key in validation){
          for (const type in validation[key]){
            for (const index in validation[key][type])
              errors.push(validation[key][type][index].message);
          }
        }

        for (const error of _.uniq(errors)){
          dispatch.toast.showToast({ type: 'error', message: error || 'Oops! Something went wrong.' });
        }
      }
    },

    async checkEmail(payload): Promise<void> {
      const response = await get(replaceUrls(apiUrls.checkEmail, { email: payload }));

      if (response && response.status === 200) {
        dispatch.auth.setEmailStatus(response.data.results?.status);
        return response.data.results?.status;
      }
    },

    async checkPhone(payload): Promise<void> {
      const response = await get(replaceUrls(apiUrls.checkCellphone, { cellphone: payload }));

      if (response && response.status === 200) {
        dispatch.auth.setPhoneStatus(response.data.results?.status);
        return response.data.results?.status;
      }
    },

    async getWallets(payload, rootState): Promise<any> {
      try {
        const response = await get(replaceUrls(apiUrls.userWallets, { accountNumber: rootState.auth.customerId }));

        if (response && response.status === 200) {
          const wallets = _.map(response.data.results, (wallet) => {
            return {
              type: wallet.type.shortName,
              typeCode: wallet.type.code,
              accountNumber: +rootState.auth.customerId,
              currency: wallet.currency,
              divisor: wallet.divisor,
              isActive: wallet.isActive,
              id: wallet.id,
              balance: wallet.balance
            };
          });

          dispatch.auth.setWallets(wallets);
          localStorage.setItem('wallets', JSON.stringify(wallets));
        }
      } catch (error: any) {
        //TODO: HANDLE ERROR
      }
    },

    async logout(): Promise<void> {
      localStorage.removeItem('token');
      localStorage.removeItem('customerId');
      localStorage.removeItem('wallets');
      dispatch.auth.setLogout();
      dispatch(replace('/'));
    },

    async tillCustomerLogout(): Promise<void> {
      localStorage.removeItem('token');
      localStorage.removeItem('customerId');
      localStorage.removeItem('wallets');
      dispatch.auth.setLogout();
    },

    async checkUser(payload, state){
      try {
        if (state.auth.customerId){
          const response = await get(`${ apiUrls.users }/${ state.auth.customerId }`);
          if (response && response.status === 200) {
            return true;
          }
          dispatch.auth.logout();
          return false;
        }
        return true;
      } catch (e){
        dispatch.auth.logout();
        return false;
        //TODO: HANDLE ERROR
      }
    },

    async getCustomer(payload, state): Promise<void> {
      try {
        if (state.auth.customerId){

          const response = await get(`${ apiUrls.users }/${ state.auth.customerId }`);
          if (response && response.status === 200) {
            dispatch.auth.setCustomerInfo(response.data.results);
          } else {
            dispatch.auth.logout();
          }
        }
      } catch (e){
        //TODO: HANDLE ERROR
      }
    },

    updateCustomer(payload): void {
      dispatch.auth.setLogin(payload);
    },

    selectWallet(payload: string, rootState): void {
      const wallets = JSON.parse(JSON.stringify(rootState.auth.customer.wallets));
      const indexOfActiveWallet = _.findIndex(wallets, { isActive: true });
      const indexOfNewWallet = _.findIndex(wallets, { type: payload });
      wallets[indexOfActiveWallet].isActive = false;
      wallets[indexOfNewWallet].isActive = true;

      dispatch.auth.setWallets(wallets);
      localStorage.setItem('wallets', JSON.stringify(wallets));
      dispatch.betslip.calculateBets(false);
    },

    selectLocalWallet(payload: AuthState['localWalletSelected']): void {
      dispatch.auth.setLocalWallet(payload);
    },

    async changePassword(payload): Promise<void> {
      try {
        const response = await post(apiUrls.tillChangePassword, payload);
        if (response.status === 200){
          dispatch(push('/'));
          dispatch.toast.showToast({ type: 'success', message: 'Password changed, you may login with your new password' });
        } else {

          dispatch.toast.showToast({ type: 'error', message: response.data.message });
        }
      } catch (error) {
        //TODO: HANDLE ERROR
      }
    },

    async tillLogin(payload, rootState): Promise<void> {
      try {
        if (rootState.auth.customer) dispatch.auth.logout();
        const newPayload = payload;

        if (payload.till === ''){
          newPayload.till = localStorage.getItem('tillId');
        }

        if (newPayload.till === '-1'){
          newPayload.till = localStorage.getItem('tillCode');
        }

        const response = await post(apiUrls.tillLogin, payload);
        if (response && response.status === 200 && !response.data.results?.changePassword) {
          const currentLogin = (new Date()).getTime() / 1000;
          const decodedToken:any = jwt.decode(response.data.results.token);
          dispatch.auth.setTillLogin(response.data.results.token);
          localStorage.setItem('till', response.data.results.token);
          localStorage.setItem('shop', response.data.results.tillSession.till.shop.code);
          localStorage.setItem('tillCode', payload.till);
          localStorage.setItem('tillId', payload.till);
          localStorage.setItem('footerTill', response.data.results.tillSession.till.code);
          localStorage.setItem('company', response.data.results.tillSession.till.shop.company.code);
          localStorage.setItem('tillUsername', payload.username);
          localStorage.setItem('tilluserId', response.data.results.userId);
          localStorage.setItem('tillSession', response.data.results.tillSession.id);
          localStorage.setItem('firstLogin', currentLogin.toString());
          localStorage.setItem('sessionStart', response.data.results.tillSession.startedAt);
          localStorage.setItem('exp', decodedToken.exp);
          localStorage.setItem('shopId',  response.data.results.tillSession.till.shop.id);
          localStorage.setItem('codePath', response.data.results.tillCodePath);

          dispatch.peripherals.updateSessionApproval((response.data.results.tillSession.till.code).toString().length >= 4);
          dispatch.peripherals.updateTillHistory((response.data.results.tillSession.till.code).toString().length >= 4);

          dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
          await dispatch.auth.getTillWallets();
          await dispatch.peripherals.getAutoTransactionsFunctions();
        } else if (response.data.results && response.data.results.changePassword && response.data.results.changePasswordReason){

          dispatch.toast.showToast({ type: 'error', message: response.data.results.changePasswordReason });
          dispatch(replace(`?token=${ response.data.results.token }`));
        } else {
          const message = response.data.results ? checkError(response.data.results) : response.data.message;
          dispatch.toast.showToast({ type: 'error', message });
          dispatch.betslip.setProcessing(false);
        }
      } catch (error: any) {
        dispatch.toast.showToast({ type: 'error', message: (error.response && error.response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    async reboot(payload):Promise<void>{
      const newPayload = payload;

      if (payload.till === ''){
        newPayload.till = localStorage.getItem('tillId');
      }

      const response = await post(apiUrls.tillLogin, newPayload);
      if (response && response.status === 200){
        if (payload.reboot){
          await post('/REBOOT', 'tillUrl');
        } else {
          await post('/SHUTDOWN', 'tillUrl');
        }
      } else {
        const message = checkError(response.data.results);
        dispatch.toast.showToast({ type: 'error', message });
      }
    },

    async tradingLogin(payload): Promise<void> {
      try {
        const response = await post(apiUrls.tradingLogin, payload);

        if (response && response.status === 200) {
          dispatch.auth.setTradingLogin(response.data.results.token);
          localStorage.setItem('tradingToken', response.data.results.token);
          dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message });
        }
      } catch (error: any) {
        dispatch.toast.showToast({ type: 'error', message: (error.response && error.response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    async tillLogout({ showInactivityMessage, hardLogout, expired=false }:
      { showInactivityMessage: boolean, hardLogout: boolean, expired: boolean }, rootState): Promise<void> {
      try {
        dispatch.lotto.resetLottoFastModeHistory();

        if (rootState.auth.customer) dispatch.auth.logout();
        if (hardLogout){
          localStorage.removeItem('tillCode');
          localStorage.removeItem('tillUsername');
          const  response = await post(apiUrls.tillLogout, { token: rootState.auth.tillUser || localStorage.getItem('till') });
          if (response && response.status === 200) {
            dispatch.auth.setTillLogin(null);
            localStorage.removeItem('till');
            localStorage.removeItem('tillId');
            localStorage.removeItem('exp');

            dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
          } else {
            dispatch.auth.setTillLogin(null);
            localStorage.removeItem('till');

            const message = checkError(response.data.results);
            dispatch.toast.showToast({ type: 'error', message });
          }

          if (showInactivityMessage) {
            dispatch.toast.showToast({ type: 'info', message: 'You were logged out due to inactivity' });
          }
          dispatch(replace('/'));
        } else {
          localStorage.removeItem('till');
          localStorage.removeItem('token');
          localStorage.removeItem('tillUser');
          localStorage.removeItem('till');
          localStorage.removeItem('exp');
          dispatch.auth.setTillLogin(null);
          await post(apiUrls.tillPause, { token: rootState.auth.tillUser || localStorage.getItem('till') });
          dispatch(replace('/'));
          if (expired){
            dispatch.toast.showToast({ type: 'error', message: 'Token has expired, login again' });
          } else if (!expired){
            dispatch.toast.showToast({ type: 'info', message: 'You are in pause mode' });
          }
          dispatch(replace('/'));
        }
      } catch (error: any) {
        localStorage.removeItem('tillCode');
        localStorage.removeItem('tillUsername');

        dispatch.toast.showToast({ type: 'error', message: (error.response && error.response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    async tradingLogout(showInactivityMessage: boolean, rootState): Promise<void> {
      try {
        const response = await post(apiUrls.tradingLogout, { token: rootState.auth.tradingUser || localStorage.getItem('tradingToken') });

        if (response && response.status === 200) {
          dispatch.auth.setTradingLogin(null);
          localStorage.removeItem('tradingToken');

          dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
        } else {
          if (response?.status === 401) {
            dispatch.auth.setTradingLogin(null);
            localStorage.removeItem('tradingToken');
          }

          dispatch.toast.showToast({ type: 'error', message: (response && response.data.message) || 'Oops! Something went wrong.' });
        }

        if (showInactivityMessage) {
          dispatch.toast.showToast({ type: 'info', message: 'You were logged out due to inactivity' });
        }
      } catch (error: any) {
        dispatch.toast.showToast({ type: 'error', message: (error.response && error.response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    async tillRecoverPassword(payload): Promise<void> {
      try {
        const response = await post(apiUrls.tillRecoverPassword, payload);

        if (response && response.status === 200) {
          dispatch.toast.showToast({ type: 'success', message: (response && response.data.message) });
        } else {
          const message = checkError(response.data.results);
          dispatch.toast.showToast({ type: 'error', message });
        }
      } catch (error: any) {
        dispatch.toast.showToast({ type: 'error', message: (error.response && error.response.data.message) || 'Oops! Something went wrong.' });
      }
    },

    updateTillUser(): void {
      dispatch.auth.setTillLogin(localStorage.getItem('till'));
    },

    updateTradingUser(): void {
      dispatch.auth.setTradingLogin(localStorage.getItem('tradingToken'));
    },

    updateHistoryWallet(payload) {
      dispatch.auth.setHistoryWallet(payload);
    },

    addNumberCustomer(payload: string) {
      dispatch.auth.setCustomerCard(payload);
    },

    async getTillWallets(){
      const response = await get(replaceUrls(apiUrls.getTillWallets, { tillId: localStorage.getItem('tillCode') }));
      if (response && response.status === 200){
        dispatch.auth.addTillWallets(response.data.results.collection);
      }
    }
  }),

  selectors: (slice) => ({
    activeWallet() {
      return slice(state => {
        if (state.customer) {
          const wallet = (state.customer.wallets && state.customer.wallets.filter(w => w.isActive)) || [];

          return (wallet.length > 0 && wallet[0]) || {};
        }

        return {};
      });
    }
  })
});
